import { useState } from "react";
import { api, type Order } from "./api";
import { NEXT_STATUS } from "./orderStatus";
import { useTranslation } from "./i18n";
import { printOrder } from "./print";

type Props = {
  order: Order;
  onTransition: (order: Order, targetStatus?: string) => void;
  onOrderUpdate?: (order: Order) => void;
  onHide?: (id: string) => void;
  isTransitioning?: boolean;
};

function customerName(order: Order): string {
  if (!order.customer) return "—";
  const { first_name, last_name, name } = order.customer;
  if (name) return name;
  return [first_name, last_name].filter(Boolean).join(" ") || "—";
}

function formatMoney(minor: number | null | undefined): string {
  if (minor == null) return "0.00";
  return (minor / 100).toFixed(2);
}

const PLATFORM_ICONS: Record<string, string> = {
  wolt: "🟦",
  uber_eats: "🟩",
  glovo: "🟨",
  pyszne: "🟧",
  bolt_food: "🟪",
};

export function OrderRow({ order, onTransition, onHide, isTransitioning = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const next = NEXT_STATUS[order.status];
  const icon = PLATFORM_ICONS[order.platform] ?? "📦";
  const currency = order.currency ?? "PLN";
  const isNew = order.status === "received";
  const isTerminal = order.status === "delivered" || order.status === "cancelled";

  return (
    <div className="row-container">
      <div className="row" onClick={() => setExpanded(!expanded)}>
        <div className="row-cell-platform">
          <span className="platform-icon">{icon}</span>
          <div>
            <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
              {order.platform.replace("_", " ")}
            </div>
            <div className="muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
              #{order.display_code ?? order.external_order_id}
              {order.is_sandbox && (
                <span style={{ 
                  backgroundColor: "#ffaa0033", 
                  color: "#ffaa00", 
                  padding: "0 4px", 
                  borderRadius: 4, 
                  fontSize: 9,
                  fontWeight: 700,
                  border: "1px solid #ffaa0066"
                }}>SANDBOX</span>
              )}
            </div>
          </div>
        </div>
        <div>
          <div>{customerName(order)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            {new Date(order.placed_at).toLocaleString()}
          </div>
        </div>
        <div style={{ fontWeight: 500 }}>
          {formatMoney(order.totals.total_minor)}{" "}
          <span className="muted" style={{ fontSize: 11 }}>{currency}</span>
        </div>
        <span className={`tag ${order.status}`}>{order.status.replace("_", " ")}</span>
        <div className="actions">
          {isNew ? (
            <>
              <button
                className="btn btn-accept"
                disabled={isTransitioning}
                onClick={(e) => {
                  e.stopPropagation();
                  onTransition(order, "accepted");
                }}
              >
                {isTransitioning ? "…" : t("orders.accept")}
              </button>
              <button
                className="btn btn-reject"
                disabled={isTransitioning}
                onClick={(e) => {
                  e.stopPropagation();
                  onTransition(order, "rejected");
                }}
              >
                {isTransitioning ? "…" : t("orders.reject")}
              </button>
            </>
          ) : next ? (
            <button
              className="btn btn-primary"
              disabled={isTransitioning}
              onClick={(e) => {
                e.stopPropagation();
                onTransition(order);
              }}
            >
              {isTransitioning ? "…" : `→ ${next}`}
            </button>
          ) : isTerminal && onHide ? (
            <button
              className="btn btn-secondary-sm"
              style={{ padding: "6px 12px", border: "1px solid var(--border)" }}
              onClick={(e) => {
                e.stopPropagation();
                onHide(order.id);
              }}
            >
              {t("orders.hide")}
            </button>
          ) : null}
          <button
            className="btn btn-print"
            onClick={async (e) => {
              e.stopPropagation();
              printOrder(order); // keep browser print as fallback or local copy
              try {
                const res = await api.printOrder(order.id);
                if (res.status === "error") {
                  console.warn("Backend print error:", res.message);
                } else {
                  console.log("Backend print success:", res.results);
                }
              } catch (err) {
                console.error("Backend print failed", err);
              }
            }}
          >
            {t("orders.print")}
          </button>
          {onHide && (
            <button
              className="btn btn-secondary-sm"
              style={{ padding: "6px 12px", border: "1px solid var(--border)" }}
              onClick={(e) => {
                e.stopPropagation();
                onHide(order.id);
              }}
            >
              {t("orders.hide")}
            </button>
          )}
          <span className={`expand-arrow ${expanded ? "expanded" : ""}`}>▾</span>
        </div>
      </div>

      {expanded && (
        <div className="order-expanded">
          {/* Customer & meta row */}
          {(order.customer || order.customer_notes) && (
            <div className="detail-meta-row">
              {order.customer?.phone && (
                <span className="detail-chip">📞 {order.customer.phone}</span>
              )}
              {order.customer?.email && (
                <span className="detail-chip">✉️ {order.customer.email}</span>
              )}
              {order.order_type && (
                <span className="detail-chip">
                  {order.order_type === "delivery" ? "🚗 Delivery" : "🏪 Takeaway"}
                </span>
              )}
            </div>
          )}

          {order.customer_notes && (
            <div className="customer-notes">
              <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>Customer note</span>
              <p style={{ margin: "4px 0 0", fontSize: 14 }}>{order.customer_notes}</p>
            </div>
          )}

          <div className="order-details-grid">
            {/* Items */}
            <div className="detail-group">
              <h4>Order Items ({order.items?.length ?? 0})</h4>
              {order.items?.map((item, idx) => (
                <div key={item.id ?? idx} className="item-block">
                  <div className="detail-item">
                    <span>
                      <span className="item-qty">{item.quantity}×</span> {item.name}
                    </span>
                    <span style={{ fontWeight: 500 }}>{formatMoney(item.total_price_minor)} {currency}</span>
                  </div>
                  {item.modifiers?.length > 0 && (
                    <div className="modifier-list">
                      {item.modifiers.map((mod, mi) => (
                        <div key={mi} className="modifier-item">
                          <span className="muted">+ {mod.name}{mod.quantity > 1 ? ` ×${mod.quantity}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {item.notes && (
                    <div className="item-note">📝 {item.notes}</div>
                  )}
                </div>
              ))}
              {(!order.items || order.items.length === 0) && (
                <div className="muted" style={{ fontSize: 13, padding: "8px 0" }}>
                  Items not available for this order
                </div>
              )}
            </div>

            {/* Financial summary */}
            <div className="detail-group">
              <h4>Financial Summary</h4>
              <div className="detail-item">
                <span className="muted">Subtotal</span>
                <span>{formatMoney(order.totals.subtotal_minor)} {currency}</span>
              </div>
              {order.totals.delivery_fee_minor != null && (
                <div className="detail-item">
                  <span className="muted">Delivery fee</span>
                  <span>{formatMoney(order.totals.delivery_fee_minor)} {currency}</span>
                </div>
              )}
              {order.totals.service_fee_minor != null && (
                <div className="detail-item">
                  <span className="muted">Service fee</span>
                  <span>{formatMoney(order.totals.service_fee_minor)} {currency}</span>
                </div>
              )}
              {order.totals.tip_minor != null && order.totals.tip_minor > 0 && (
                <div className="detail-item">
                  <span className="muted">💰 Tip</span>
                  <span style={{ color: "#34d399" }}>{formatMoney(order.totals.tip_minor)} {currency}</span>
                </div>
              )}
              {order.totals.discount_minor != null && order.totals.discount_minor > 0 && (
                <div className="detail-item">
                  <span className="muted">🏷️ Discount</span>
                  <span style={{ color: "#fbbf24" }}>−{formatMoney(order.totals.discount_minor)} {currency}</span>
                </div>
              )}
              <div className="detail-item detail-total">
                <span>Total</span>
                <span>{formatMoney(order.totals.total_minor)} {currency}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
